/**
 * COMPANY ATTACHMENTS — the ONE decision point for their wording and their
 * pure rules.
 *
 * The same standing as `src/lib/credit-notes.ts`, `src/lib/company-projects.ts`
 * and `src/lib/docusign.ts`: every surface that lists an attachment, sizes one,
 * names who added it or refuses one comes through here, so no two of them can
 * spell one fact differently or word one refusal two ways.
 *
 * ⛔ **NOTHING HERE FETCHES OR WRITES. IT DECIDES.** There are no hooks, no
 * `api` imports and no request builders, and none may be added. The panel owns
 * its own requests, its own upload progress and its own draft state; this
 * module answers *"given what the server said about this row, what should the
 * screen say, and what should it refuse before spending any bytes?"* — which is
 * the half that must not be reimplemented per surface.
 *
 * ## A ROW IS EITHER A FILE OR A LINK, AND THAT IS WHY HALF OF THIS FILE EXISTS
 *
 * `kind` is the discriminator and the two shapes are genuinely disjoint: a file
 * row carries `fileName`, `contentType` and `sizeBytes` and no `url`; a link row
 * carries a `url` and none of the other three. So **every nullable field on
 * `CompanyAttachment` is null for a STRUCTURAL reason on one of the two kinds**,
 * not because a value is missing — and a helper that coalesces one of them is
 * not being defensive, it is inventing a fact about the other kind. The two
 * places that bite are {@link formatAttachmentSize}, where `null` means *this
 * row has no size at all* rather than *zero bytes*, and
 * {@link attachmentSubtitle}, which branches on `kind` and never on which
 * fields happen to be populated.
 *
 * ⛔ **BRANCH ON `kind`, NEVER ON A FIELD'S PRESENCE.** The same rule this app
 * already applies to `xeroPush.status`: a field test agrees with the
 * discriminator on every row that exists today and files the first row of a
 * third kind under whichever branch it happens to satisfy — a confident wrong
 * answer rather than a blank.
 *
 * ## ⛔ THE NO-HELPER-TEXT PROHIBITION, AND WHAT THIS FILE IS
 *
 * This app forbids helper text outright: no `hint` on a field, no `description`
 * on a `Modal` or a `Panel`, no explanatory sentence under a control.
 * **Everything sentence-shaped in this file is one of the documented
 * EXCEPTIONS**, and each one says at its own definition which exception it is
 * claiming, so a future helper-text sweep can tell them apart rather than
 * deleting a refusal about a file somebody is waiting on.
 *
 * ```
 * EXCEPTION 1  a REFUSAL — the server said no, or will, and why
 * EXCEPTION 4  a FACT ABOUT THE DATA the reader cannot otherwise see
 * ```
 *
 * {@link oversizeRefusal} and {@link typeRefusal} claim EXCEPTION 1.
 * {@link formatAttachmentSize}, {@link uploaderLabel} and
 * {@link attachmentSubtitle} are VALUE LABELS, which are not helper text at all
 * and need no exception — the prohibition is about prose attached to a CONTROL,
 * and a rendered value is the thing the control exists to show.
 *
 * ⛔ **So no `hint`, `description`, `placeholder` or explanatory export may be
 * added here**, however natural one would look beside the two refusals. A
 * sentence telling somebody what they *may* attach, rendered before they have
 * tried to attach anything, is exactly the prose the rule forbids — the accept
 * attribute and the refusals are how that fact is carried.
 *
 * ## ⚠️ THE ALLOW-LIST IS A DUPLICATE OF A SERVER CONSTANT
 *
 * {@link ATTACHMENT_TYPES} mirrors the server's own list by hand. **Nothing
 * keeps them in step** — not a type, not a build step, not a test — and the two
 * repositories deploy separately, so this list can be right on the day it is
 * written and wrong after a migration nobody here sees.
 *
 * ⚠️ **The consequence when they drift, in the direction that costs the
 * reader:** this app offers a type the server then refuses **at confirm, after
 * the bytes have already been paid for** — a full upload of a large file over a
 * slow connection, ending in a refusal that could have been given instantly.
 * **The other direction is cheaper and quieter**: a type the server would have
 * accepted is simply not offered, and the reader attaches it as a link instead
 * without ever knowing the picker was narrower than it needed to be.
 *
 * 🔴 **THE SERVER IS THE ENFORCEMENT AND THIS IS THE COURTESY.** Nothing here
 * may be described, labelled or relied on as a rule: `accept` is advisory and
 * every browser lets a person pick "All files" or drag one in past it, and
 * {@link isAllowedAttachmentType} runs on a `File.type` the *platform* decided —
 * Chrome on Windows reports a `.csv` as `application/vnd.ms-excel` where another
 * platform reports `text/csv`, and both spellings are on this list precisely so
 * that divergence does not surface as a refusal. **When this list and the
 * server's disagree, the server is right.**
 *
 * ## SEED EMERGE
 *
 * Every user-facing string in this app spells the product **SEED EMERGE**, never
 * "Emerge". ⚠️ **No string here needs it at all**, which is deliberate rather
 * than an omission: a refusal reads better as a statement about the file than as
 * a statement about the product, and naming the system in a sentence about a
 * `.key` file invites the reading that some *other* system would have taken it.
 */

import type { CompanyAttachment } from './types';

/*
 * STRUCTURAL parameter types, so a company attachment and a task attachment
 * both satisfy the helpers below without either importing the other's type.
 * They are `Pick`s of the company row rather than fresh declarations, so the
 * field shapes cannot drift from the wire type they were written against.
 */
type AttachmentUploader = CompanyAttachment['uploadedBy'];
type AttachmentSubtitleSource = Pick<
  CompanyAttachment,
  'kind' | 'url' | 'sizeBytes' | 'fileName'
>;

/* --- what may be attached ------------------------------------------------ */

/**
 * The ceiling on an uploaded file, in bytes — **25 MB**, one limit everywhere
 * (owner's decision). ⛔ **This constant is the ONLY place the number is
 * written**: the refusal derives its wording from it, and company and task
 * attachments both import it, so moving it moves every surface at once.
 *
 * ⚠️ **`1024`-based, and that is load-bearing rather than pedantic.**
 * {@link formatAttachmentSize} divides by 1024 too, so this constant renders as
 * a round **"25 MB"** in {@link oversizeRefusal}. Had either half used 1000 the
 * refusal would read *"26.2 MB, over the 25 MB limit"* — a sentence that
 * contradicts itself on a file that is exactly at the boundary, which is the one
 * file somebody argues about. **The constant and the formatter move together or
 * neither moves.**
 */
export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

/**
 * The content types the server stores, with the extensions that produce them.
 *
 * ⚠️ **ONE table, two consumers** — {@link isAllowedAttachmentType} and
 * {@link ATTACHMENT_ACCEPT}. A separate extension list beside a separate MIME
 * list would agree the day they were written and drift on the first type added,
 * and the drift is silent in the worst direction: a type the check accepts and
 * the picker will not show, so the reader cannot reach a file the app would have
 * taken.
 *
 * ⛔ **It is module-private on purpose.** Exporting it would give a panel a
 * second way to ask the same question — a hand-rolled `.includes()` over the
 * list, which is how one surface comes to accept a type another refuses. The two
 * exported derivations are the whole interface.
 *
 * ⚠️ **`.csv` appears under two content types because platforms disagree about
 * it**, and `.jpg`/`.jpeg` are two extensions for one type. Neither is a
 * duplicate to tidy away.
 */
const ATTACHMENT_TYPES: readonly { contentType: string; extensions: readonly string[] }[] = [
  { contentType: 'application/pdf', extensions: ['.pdf'] },
  { contentType: 'text/plain', extensions: ['.txt'] },
  { contentType: 'text/csv', extensions: ['.csv'] },
  { contentType: 'text/markdown', extensions: ['.md'] },
  {
    contentType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    extensions: ['.docx'],
  },
  {
    contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    extensions: ['.xlsx'],
  },
  {
    contentType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    extensions: ['.pptx'],
  },
  { contentType: 'application/msword', extensions: ['.doc'] },
  // Chrome on Windows reports a `.csv` as this, which is why `.csv` is listed
  // here as well as under `text/csv`. Dropping either spelling refuses a
  // spreadsheet export on one platform and takes it on another.
  { contentType: 'application/vnd.ms-excel', extensions: ['.xls', '.csv'] },
  { contentType: 'application/rtf', extensions: ['.rtf'] },
  { contentType: 'image/png', extensions: ['.png'] },
  { contentType: 'image/jpeg', extensions: ['.jpg', '.jpeg'] },
  // Added 2026-09-25 (owner): saved emails and more image formats.
  { contentType: 'message/rfc822', extensions: ['.eml'] },
  { contentType: 'application/vnd.ms-outlook', extensions: ['.msg'] },
  { contentType: 'image/gif', extensions: ['.gif'] },
  { contentType: 'image/webp', extensions: ['.webp'] },
  { contentType: 'image/heic', extensions: ['.heic'] },
  { contentType: 'image/heif', extensions: ['.heif'] },
];

const ALLOWED_CONTENT_TYPES = new Set(ATTACHMENT_TYPES.map((entry) => entry.contentType));

/**
 * The content type to upload a file under: the browser's own `File.type` when
 * the server accepts it, otherwise the type its EXTENSION maps to in
 * {@link ATTACHMENT_TYPES}, otherwise `''` (which is then refused).
 *
 * ⚠️ **Why the fallback exists.** Browsers commonly report `''` for `.msg`,
 * `.eml`, `.heic` and `.md`, because the OS has no type registered for them —
 * so without it an Outlook email could never be attached at all. `File.type` is
 * itself derived from the extension, so this claims nothing about the bytes that
 * the browser would not have claimed; the server re-checks the type either way.
 *
 * ⛔ **Call it ONCE and use the answer twice** — for the signed target and for
 * the PUT. The signature pins `content-type`, so two calls that could disagree
 * are a 403 from storage.
 */
export function attachmentContentType(file: File): string {
  if (isAllowedAttachmentType(file.type)) return file.type;
  const dot = file.name.lastIndexOf('.');
  if (dot === -1) return '';
  const extension = file.name.slice(dot).toLowerCase();
  return ATTACHMENT_TYPES.find((entry) => entry.extensions.includes(extension))?.contentType ?? '';
}

/**
 * The file input's `accept` attribute, derived from {@link ATTACHMENT_TYPES}.
 *
 * **Content types AND extensions, both**, because neither alone is enough: some
 * platforms match an `accept` entry against the type they would report and some
 * against the extension, and a `.md` or a `.csv` is exactly where they disagree.
 * Listing both costs nothing and is the difference between a reader seeing their
 * file in the picker and concluding it cannot be attached.
 *
 * ⛔ **IT IS NOT A CHECK AND MUST NEVER BE TREATED AS ONE.** Every browser
 * offers an "All files" escape in the picker, and a drag-and-drop bypasses
 * `accept` entirely. It narrows what is easy to choose;
 * {@link isAllowedAttachmentType} is what this app refuses on, and the server is
 * what actually enforces.
 */
export const ATTACHMENT_ACCEPT = [
  ...ATTACHMENT_TYPES.map((entry) => entry.contentType),
  ...ATTACHMENT_TYPES.flatMap((entry) => entry.extensions),
].join(',');

/**
 * Whether the server will store a file of this content type — **as far as this
 * app can tell**.
 *
 * ⚠️ **Normalised before it is compared, and only in ways that cannot
 * over-permit:**
 *
 * - **lower-cased**, because a MIME type is case-insensitive by specification.
 *   A browser emits lower case, so this only ever rescues a type that arrived
 *   from somewhere else already stored.
 * - **parameters stripped** (`text/csv;charset=UTF-8` → `text/csv`), because the
 *   parameter is metadata about the bytes rather than a different type.
 *
 * ⚠️ **The parameter half is the one with an unmeasured cost, so it is stated
 * rather than buried: whether the server strips parameters before comparing is
 * NOT known here.** If it does not, a parameterised type passes this check and
 * is refused at confirm. It is stripped anyway because `File.type` from a
 * browser is bare in practice — so the upload path, which is the path where that
 * cost lands, is unaffected — while a stored row read back from the server may
 * legitimately carry one, and there this predicate only ever describes a row
 * that already exists.
 *
 * ⛔ **A `false` here is a reason to refuse before uploading. It is never
 * evidence that a stored row is invalid** — this list is a copy and the server's
 * is the original, so a stored attachment whose type is not on it is far more
 * likely to mean this list is stale than that the row is wrong. Never hide,
 * strike through or warn about an existing attachment on the strength of it.
 */
export function isAllowedAttachmentType(contentType: string): boolean {
  const semicolon = contentType.indexOf(';');
  const bare = (semicolon === -1 ? contentType : contentType.slice(0, semicolon))
    .trim()
    .toLowerCase();
  return ALLOWED_CONTENT_TYPES.has(bare);
}

/* --- rendering a row ----------------------------------------------------- */

/**
 * ⚠️ **`GB` is reachable and is not defensive padding.**
 * {@link oversizeRefusal} formats the size of a file the reader just tried to
 * attach, which is bounded by nothing — a 3 GB video is one mis-click away — and
 * a refusal reading *"3072.0 MB"* is a refusal nobody can read at a glance.
 */
const SIZE_UNITS = ['B', 'KB', 'MB', 'GB'] as const;

/** Paired with {@link MAX_ATTACHMENT_BYTES}; see the note there. */
const SIZE_STEP = 1024;

/**
 * A file's size, as the subtitle and the refusals both spell it.
 *
 * ```
 * null      ->  ''          a LINK row — it has no size, rather than a size of zero
 * 0         ->  '0 B'       a real, empty FILE
 * 1024      ->  '1 KB'
 * 1536      ->  '1.5 KB'
 * 25264128  ->  '24.1 MB'
 * ```
 *
 * 🔴 **`null` RETURNS AN EMPTY STRING AND MUST NEVER RETURN `'0 B'`.** A link
 * row has no size at all, so `0 B` there is a confident, specific, wrong claim
 * about a file that does not exist — and it is wrong in the reassuring
 * direction, because it reads as a real measurement of a real empty file rather
 * than as a bug. **`0` and `null` are different facts and this function is where
 * they are kept apart**; a `?? 0` anywhere between the wire and this call turns
 * the second into the first.
 *
 * ⚠️ **A non-finite or negative figure also returns `''`**, for the same reason
 * in a different direction: there is no honest number to print, and printing a
 * dishonest one is worse than printing nothing. The caller already renders a
 * missing subtitle gracefully, because a link row produces one on every row.
 *
 * ⚠️ **One decimal from `KB` up, none at `B`, and a trailing `.0` stripped.**
 * A tenth of a kilobyte is below anything a reader acts on, and `1.0 MB` reads
 * as machine output where `1 MB` reads as a size. Bytes take no decimal at all
 * because a fractional byte is not a thing.
 */
export function formatAttachmentSize(bytes: number | null): string {
  if (bytes === null || !Number.isFinite(bytes) || bytes < 0) return '';

  let value = bytes;
  let unit: string = 'B';
  /*
   * `entries()` rather than an index, because `noUncheckedIndexedAccess` makes
   * `SIZE_UNITS[i]` a `string | undefined` that would need a fallback — and a
   * fallback unit is a silent wrong answer waiting for an off-by-one.
   *
   * The `i === length - 1` test is what stops the last unit being divided past:
   * without it a figure in terabytes divides once more than there are units and
   * prints a number three orders of magnitude too small, in GB, looking
   * perfectly plausible.
   */
  for (const [i, name] of SIZE_UNITS.entries()) {
    unit = name;
    if (value < SIZE_STEP || i === SIZE_UNITS.length - 1) break;
    value /= SIZE_STEP;
  }

  const rendered =
    unit === 'B' ? String(Math.round(value)) : value.toFixed(1).replace(/\.0$/, '');
  return `${rendered} ${unit}`;
}

/**
 * Who added an attachment — **a three-way byline, and `null` is NOT "Unknown".**
 *
 * ```
 * null                          ->  'Former colleague'
 * { name: 'Priya Raman', … }    ->  'Priya Raman'
 * { name: '', email: 'a@b.c' }  ->  'a@b.c'
 * ```
 *
 * 🔴 **`uploadedBy: null` MEANS THE ACCOUNT WAS DELETED.** `d_users` has no
 * active, deleted or disabled column, so **deletion is the only way a person
 * leaves this system** — which makes a null uploader a person's action that has
 * lost its name, never an action nobody took. `'Unknown'`, `'Nobody'`, `'—'` and
 * `''` are all wrong in the same direction: they read as *we do not know whether
 * anybody did this*, about a row somebody definitely added.
 *
 * ⚠️ **The vocabulary is `NotesPanel.tsx`'s, taken rather than invented.** That
 * file's `bylineOf` renders the identical fact as **`'Former colleague'`** for a
 * row label, and two different words for one fact on two screens is precisely
 * the drift this module exists to prevent — a reader meeting "Former colleague"
 * on a note and "Unknown" on an attachment has no way to tell they are the same
 * situation. ⛔ **If that wording ever changes, it changes in both places.**
 *
 * ⚠️ **`Staff #<id>` is a FOURTH rung and is NOT a fourth meaning.** A record
 * with neither a name nor an email is a live account that names nobody, which is
 * a different fact from a deleted one — so it must not fall through to *Former
 * colleague*, which would report a colleague as gone. It is the established
 * last resort in this app (`name.trim() || email.trim() || 'Staff #id'`), and it
 * exists because an empty string in a value slot reads as a missing record
 * rather than as an unnamed one.
 *
 * ⚠️ **Trimmed before it is tested.** A name of `'   '` is not empty to `=== ''`
 * and renders as a byline that looks like a rendering fault; this is the same
 * three-token expression anybody would reproduce from memory and get subtly
 * wrong, which is why it is written once.
 */
export function uploaderLabel(uploadedBy: AttachmentUploader): string {
  if (uploadedBy === null) return 'Former colleague';
  return (
    uploadedBy.name?.trim() ||
    uploadedBy.email?.trim() ||
    `Staff #${uploadedBy.id}`
  );
}

/**
 * The small grey line under an attachment's title.
 *
 * ```
 * a file  ->  '1.4 MB · Q3 report.pdf'
 * a link  ->  'drive.google.com'
 * ```
 *
 * ⚠️ **It branches on `kind`, never on which fields are populated** — see the
 * header. A future third kind falls through to `''` rather than being rendered
 * as whichever of the two it happens to resemble.
 *
 * ⚠️ **Each half degrades to the other on its own**, rather than to a
 * placeholder: a file row with no recorded size reads as just its name, a file
 * row with no name reads as just its size, and a row with neither renders
 * nothing at all. The `filter(Boolean).join(' · ')` idiom is this app's
 * established way of doing that, and it is what keeps a stray separator off a
 * half-populated row.
 *
 * ## 🔴 THE HOSTNAME IS PARSED DEFENSIVELY, AND THAT IS NOT OPTIONAL
 *
 * `new URL()` **THROWS** on a malformed string, and this function runs inside a
 * render — so a single bad row would not blank a cell, it would throw during
 * rendering, which React escalates to **unmounting the tree**. One attachment
 * takes the whole page.
 *
 * ⚠️ **And a bad row is reachable**: the server checked only the `http(s)://`
 * prefix, so `https://` with nothing after it, or a pasted string with a space
 * in it, is stored and read back. **Falling back to the raw URL is the correct
 * answer** — it is what the person typed, it is what they will recognise, and it
 * is strictly more informative than any placeholder.
 *
 * ⚠️ **An empty hostname falls back too.** `new URL('mailto:a@b.c')` does not
 * throw and yields `hostname === ''`, which would render an empty subtitle that
 * looks like a missing value rather than an unusual URL.
 *
 * ⛔ **No `www.` stripping, no truncation, no scheme badge.** The hostname is
 * shown as the URL states it; each of those would be this module deciding the
 * data reads better than it is, on a value a person pasted deliberately.
 */
export function attachmentSubtitle(attachment: AttachmentSubtitleSource): string {
  if (attachment.kind === 'link') {
    const url = attachment.url;
    if (url === null) return '';
    try {
      return new URL(url).hostname || url;
    } catch {
      return url;
    }
  }

  if (attachment.kind === 'file') {
    return [formatAttachmentSize(attachment.sizeBytes), attachment.fileName?.trim() ?? '']
      .filter((part) => part !== '')
      .join(' · ');
  }

  /*
   * A kind this build has not heard of. Saying nothing is the only honest
   * answer: rendering it through either branch above would describe it as
   * something it is not, and this app deploys separately from the API, so a
   * newer server really can send one.
   */
  return '';
}

/* --- refusals ------------------------------------------------------------ */

/**
 * A blank file name would leave a refusal starting mid-sentence (`" is 24.1
 * MB…"`), which reads as a rendering fault rather than as a refusal. `File.name`
 * is a string the platform supplies and is not guaranteed to be meaningful.
 */
function namedFile(fileName: string): string {
  return fileName.trim() || 'That file';
}

/**
 * **EXCEPTION 1 — a REFUSAL.** The file is too big to upload.
 *
 * ⚠️ **It names the FILE**, because a refusal raised over a multi-file selection
 * that does not say which file is one the reader cannot act on: they are told
 * something is too big and left to work out which of six it was.
 *
 * ⚠️ **It names the file's OWN size as well as the limit**, which is the half
 * that decides what they do next — a file 10% over is worth compressing and one
 * ten times over is not, and the limit alone cannot tell them which they have.
 *
 * 🔴 **BOTH FIGURES COME FROM {@link formatAttachmentSize}, INCLUDING THE
 * LIMIT.** The sentence puts two sizes side by side, so they must be scaled and
 * rounded identically or the refusal argues with itself — *"26.2 MB, over the 25
 * MB limit"* on a file that is exactly at the boundary. Deriving the limit
 * through the same formatter rather than writing "25 MB" by hand also means
 * moving {@link MAX_ATTACHMENT_BYTES} moves this sentence, with nothing to
 * remember.
 *
 * ⚠️ **"Attach it as a link instead" is part of the refusal, not advice.** It is
 * true — a row is either a file or a link — and it is the only route the reader
 * has to getting this document onto the record at all. A refusal with no way out
 * is the one thing worse than a refusal.
 */
export function oversizeRefusal(fileName: string, bytes: number): string {
  const size = formatAttachmentSize(bytes);
  const limit = formatAttachmentSize(MAX_ATTACHMENT_BYTES);
  /*
   * A file whose size could not be read still gets a refusal — the caller has
   * already decided it is over the limit, and dropping the clause is better than
   * printing an empty one. The limit is always printable, so the sentence never
   * loses the actionable half.
   */
  const measured = size === '' ? 'is too big' : `is ${size}, over the ${limit} limit`;
  return `${namedFile(fileName)} ${measured}. Attach it as a link instead.`;
}

/**
 * **EXCEPTION 1 — a REFUSAL.** The file is not a type that can be stored.
 *
 * ⚠️ **It names the FILE**, for the same reason {@link oversizeRefusal} does.
 *
 * ⚠️ **It names CATEGORIES rather than the list.** Twelve content types is not a
 * sentence anybody reads, and the two spellings of `.csv` would make it read as
 * a mistake; the categories tell somebody whether their file is close to
 * qualifying, which is what they need to decide between converting it and
 * linking to it.
 *
 * ⛔ **It does not say WHICH type the file is.** The reported type is the
 * platform's guess — the same guess this app has just declined to recognise — so
 * quoting it back would explain a refusal with a string the reader has no reason
 * to trust and cannot change.
 */
export function typeRefusal(fileName: string): string {
  return `${namedFile(fileName)} can't be attached. PDFs, documents, spreadsheets, slides, text files, emails and images can — or attach it as a link instead.`;
}
